/**
 * AgentAnalyticsPanel
 * ──────────────────────────────────────────────────────────────────────
 * Per-agent performance analytics with filters + CSV / printable exports.
 *
 * Displays for each agent (or the aggregate view):
 *   • Total tasks / calls handled
 *   • Pending vs Confirmed vs Cancelled
 *   • Conversion / confirmation rate
 *   • 14-day spark bar-chart (confirmed vs cancelled)
 *   • Filterable by date range, agent, appt type
 *
 * Exports:
 *   • Unconfirmed patient list (for manual outreach) — CSV + printable sheet
 *   • Scenario report (by agent + date range + appt type) — CSV + printable
 *
 * Demo-only. All data comes from `agents` + the embedded
 * `DEMO_UNCONFIRMED_PATIENTS` + `DEMO_PERFORMANCE_SERIES` fixtures.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import {
  Download, Printer, Filter, TrendingUp, TrendingDown, Phone, MessageSquareText,
  X, Calendar, Users, AlertTriangle, CheckCircle2, Clock, Zap, Send, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../../contexts/AppModeContext';

/* ── Demo fixture: last 14 days per-agent daily counts ── */
const DEMO_PERFORMANCE_SERIES = {
  'da-1': [
    { day: 'Apr 09', confirmed: 18, cancelled: 2, pending: 3 },
    { day: 'Apr 10', confirmed: 21, cancelled: 1, pending: 2 },
    { day: 'Apr 11', confirmed: 17, cancelled: 3, pending: 4 },
    { day: 'Apr 12', confirmed: 14, cancelled: 2, pending: 2 },
    { day: 'Apr 13', confirmed: 9, cancelled: 0, pending: 1 },
    { day: 'Apr 14', confirmed: 19, cancelled: 2, pending: 3 },
    { day: 'Apr 15', confirmed: 20, cancelled: 2, pending: 2 },
    { day: 'Apr 16', confirmed: 22, cancelled: 1, pending: 2 },
    { day: 'Apr 17', confirmed: 18, cancelled: 2, pending: 3 },
    { day: 'Apr 18', confirmed: 16, cancelled: 3, pending: 4 },
    { day: 'Apr 19', confirmed: 12, cancelled: 1, pending: 2 },
    { day: 'Apr 20', confirmed: 8, cancelled: 0, pending: 1 },
    { day: 'Apr 21', confirmed: 20, cancelled: 2, pending: 2 },
    { day: 'Apr 22', confirmed: 19, cancelled: 2, pending: 3 },
  ],
  'da-2': [
    { day: 'Apr 09', confirmed: 7, cancelled: 1, pending: 2 },
    { day: 'Apr 10', confirmed: 9, cancelled: 1, pending: 1 },
    { day: 'Apr 11', confirmed: 6, cancelled: 2, pending: 2 },
    { day: 'Apr 12', confirmed: 5, cancelled: 1, pending: 1 },
    { day: 'Apr 13', confirmed: 3, cancelled: 0, pending: 0 },
    { day: 'Apr 14', confirmed: 8, cancelled: 1, pending: 1 },
    { day: 'Apr 15', confirmed: 7, cancelled: 1, pending: 2 },
    { day: 'Apr 16', confirmed: 9, cancelled: 0, pending: 1 },
    { day: 'Apr 17', confirmed: 7, cancelled: 1, pending: 2 },
    { day: 'Apr 18', confirmed: 6, cancelled: 2, pending: 2 },
    { day: 'Apr 19', confirmed: 4, cancelled: 1, pending: 1 },
    { day: 'Apr 20', confirmed: 2, cancelled: 0, pending: 0 },
    { day: 'Apr 21', confirmed: 8, cancelled: 1, pending: 1 },
    { day: 'Apr 22', confirmed: 6, cancelled: 1, pending: 1 },
  ],
  'da-3': [
    { day: 'Apr 09', confirmed: 13, cancelled: 1, pending: 1 },
    { day: 'Apr 10', confirmed: 15, cancelled: 2, pending: 2 },
    { day: 'Apr 11', confirmed: 12, cancelled: 1, pending: 1 },
    { day: 'Apr 12', confirmed: 10, cancelled: 0, pending: 1 },
    { day: 'Apr 13', confirmed: 6, cancelled: 0, pending: 0 },
    { day: 'Apr 14', confirmed: 14, cancelled: 1, pending: 2 },
    { day: 'Apr 15', confirmed: 16, cancelled: 2, pending: 1 },
    { day: 'Apr 16', confirmed: 18, cancelled: 1, pending: 2 },
    { day: 'Apr 17', confirmed: 13, cancelled: 1, pending: 1 },
    { day: 'Apr 18', confirmed: 11, cancelled: 2, pending: 2 },
    { day: 'Apr 19', confirmed: 8, cancelled: 1, pending: 1 },
    { day: 'Apr 20', confirmed: 5, cancelled: 0, pending: 0 },
    { day: 'Apr 21', confirmed: 15, cancelled: 1, pending: 1 },
    { day: 'Apr 22', confirmed: 14, cancelled: 2, pending: 2 },
  ],
};

const DEMO_UNCONFIRMED_PATIENTS = [
  { id: 'u1', patient: 'James Taylor', phone: '(512) 555-0188', apptTime: 'Today 11:00 AM', apptType: 'Wellness', provider: 'Dr. Wilson', lastAttempt: 'Voicemail · 8:14 AM', agent: 'Appointment Reminder Agent', agentId: 'da-1', noShowRisk: 35 },
  { id: 'u2', patient: 'David Wilson', phone: '(512) 555-0219', apptTime: 'Today 2:00 PM', apptType: 'New Patient', provider: 'Dr. Wilson', lastAttempt: 'No answer · 9:02 AM', agent: 'No-Show Rescue Agent', agentId: 'da-2', noShowRisk: 68 },
  { id: 'u3', patient: 'Maria Garcia', phone: '(512) 555-0334', apptTime: 'Today 3:30 PM', apptType: 'Follow-up', provider: 'Dr. Chen', lastAttempt: 'Text sent · 7:45 AM', agent: 'Appointment Reminder Agent', agentId: 'da-1', noShowRisk: 18 },
  { id: 'u4', patient: 'Robert Johnson', phone: '(512) 555-0441', apptTime: 'Tomorrow 9:00 AM', apptType: 'Sick Visit', provider: 'Dr. Chen', lastAttempt: 'Call back requested', agent: 'Appointment Reminder Agent', agentId: 'da-1', noShowRisk: 42 },
  { id: 'u5', patient: 'Lisa Brown', phone: '(512) 555-0592', apptTime: 'Tomorrow 10:30 AM', apptType: 'Follow-up', provider: 'Dr. Chen', lastAttempt: 'Voicemail · Yest 4:00 PM', agent: 'Appointment Reminder Agent', agentId: 'da-1', noShowRisk: 15 },
];

const DATE_RANGES = [
  { id: 'today', label: 'Today', days: 1 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '14d', label: 'Last 14 days', days: 14 },
];

const APPT_TYPES = ['All', 'New Patient', 'Follow-up', 'Sick Visit', 'Wellness', 'Walk-in'];

/* ── CSV helper: RFC-4180-ish escaping ── */
const toCsv = (rows, columns) => {
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(typeof c.fn === 'function' ? c.fn(r) : r[c.key])).join(',')).join('\n');
  return `${header}\n${body}\n`;
};

const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const printHtml = (title, htmlBody) => {
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <style>
      body{font-family:-apple-system,system-ui,sans-serif;padding:28px;color:#111;max-width:820px;margin:0 auto}
      h1{font-size:18px;margin:0 0 4px}
      .sub{font-size:11px;color:#777;margin:0 0 16px}
      table{width:100%;border-collapse:collapse;margin-top:8px;font-size:11px}
      th,td{padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:left}
      th{background:#f9fafb;font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280}
      .kpi-row{display:flex;gap:14px;margin:12px 0 0}
      .kpi{flex:1;border:1px solid #e5e7eb;border-radius:10px;padding:12px}
      .kpi-val{font-size:22px;font-weight:700}
      .kpi-lbl{font-size:10px;color:#6b7280;text-transform:uppercase}
      @media print{body{padding:0}button{display:none}}
    </style></head><body>${htmlBody}
    <script>window.onload=()=>setTimeout(()=>window.print(),250);</script>
    </body></html>`);
  w.document.close();
};

/* ── Simple 14-day bar sparkline (confirmed vs cancelled) ── */
const SparkBars = ({ series }) => {
  const max = Math.max(1, ...series.map((d) => d.confirmed + d.cancelled + d.pending));
  return (
    <div className="flex items-end gap-[2px] h-12" data-testid="spark-bars">
      {series.map((d, i) => {
        const total = d.confirmed + d.cancelled + d.pending;
        const h = (total / max) * 100;
        return (
          <div key={i} className="flex-1 relative group" title={`${d.day} · ${d.confirmed}✓ ${d.cancelled}✕ ${d.pending}…`}>
            <div className="absolute bottom-0 w-full rounded-t-sm" style={{ height: `${(d.cancelled / max) * 100}%`, background: '#fca5a5' }} />
            <div className="absolute bottom-0 w-full rounded-t-sm" style={{ bottom: `${(d.cancelled / max) * 100}%`, height: `${(d.pending / max) * 100}%`, background: '#fde68a' }} />
            <div className="absolute bottom-0 w-full rounded-t-sm" style={{ bottom: `${((d.cancelled + d.pending) / max) * 100}%`, height: `${(d.confirmed / max) * 100}%`, background: '#86efac' }} />
            <div className="w-full" style={{ height: `${h}%`, background: 'transparent' }} />
          </div>
        );
      })}
    </div>
  );
};

const AgentAnalyticsPanel = ({ agents, demoSends = [] }) => {
  const { isDemo } = useAppMode();
  const [dateRange, setDateRange] = useState('14d');
  const [agentFilter, setAgentFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [campaignSends, setCampaignSends] = useState([]);
  const [campaignTotals, setCampaignTotals] = useState({ sent: 0, failed: 0, total: 0 });

  /* Merge demo-generated sends (from Send-now in demo mode) on top of the
     backend-fetched list. Demo sends carry `send_id` prefix "demo_" so
     they're easy to distinguish. */
  const mergedSends = useMemo(() => {
    const combined = [...demoSends, ...campaignSends];
    return combined;
  }, [demoSends, campaignSends]);
  const mergedTotals = useMemo(() => {
    if (!demoSends.length) return campaignTotals;
    const sent = campaignTotals.sent + demoSends.filter((s) => s.status === 'sent').length;
    const failed = campaignTotals.failed + demoSends.filter((s) => s.status === 'failed').length;
    return { sent, failed, total: sent + failed };
  }, [campaignTotals, demoSends]);

  /* Poll campaign sends — survives navigation and reflects Run-now clicks. */
  useEffect(() => {
    let live = true;
    const fetchSends = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ariaone-token') || '';
        const { data } = await axios.get(`${API}/api/echo/agents/campaign/sends?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!live) return;
        setCampaignSends(data.items || []);
        setCampaignTotals(data.totals || { sent: 0, failed: 0, total: 0 });
      } catch (_) { /* silent — endpoint optional */ }
    };
    fetchSends();
    const i = setInterval(fetchSends, 8000);
    return () => { live = false; clearInterval(i); };
  }, []);

  const activeAgents = useMemo(() => {
    if (agentFilter === 'all') return agents;
    return agents.filter((a) => a.id === agentFilter);
  }, [agents, agentFilter]);

  const rangeDays = DATE_RANGES.find((r) => r.id === dateRange)?.days || 14;

  const totals = useMemo(() => {
    let confirmed = 0;
    let cancelled = 0;
    let pending = 0;
    activeAgents.forEach((a) => {
      const s = (DEMO_PERFORMANCE_SERIES[a.id] || []).slice(-rangeDays);
      s.forEach((d) => {
        confirmed += d.confirmed;
        cancelled += d.cancelled;
        pending += d.pending;
      });
    });
    const handled = confirmed + cancelled + pending;
    const conversion = handled > 0 ? Math.round((confirmed / handled) * 100) : 0;
    return { confirmed, cancelled, pending, handled, conversion };
  }, [activeAgents, rangeDays]);

  const filteredUnconfirmed = useMemo(() => DEMO_UNCONFIRMED_PATIENTS.filter((u) => (
    (agentFilter === 'all' || u.agentId === agentFilter)
    && (typeFilter === 'All' || u.apptType === typeFilter)
  )), [agentFilter, typeFilter]);

  /* ── Exports ── */
  const exportUnconfirmedCsv = () => {
    const cols = [
      { key: 'patient', label: 'Patient' },
      { key: 'phone', label: 'Phone' },
      { key: 'apptTime', label: 'Appointment' },
      { key: 'apptType', label: 'Visit type' },
      { key: 'provider', label: 'Provider' },
      { key: 'lastAttempt', label: 'Last attempt' },
      { key: 'agent', label: 'Agent' },
      { key: 'noShowRisk', label: 'No-show %' },
    ];
    downloadCsv(`unconfirmed-patients_${dateRange}.csv`, toCsv(filteredUnconfirmed, cols));
    toast.success(`Exported ${filteredUnconfirmed.length} unconfirmed patient(s) as CSV`);
  };

  const printUnconfirmed = () => {
    const rows = filteredUnconfirmed.map((u) => `
      <tr>
        <td>${u.patient}</td><td>${u.phone}</td><td>${u.apptTime}</td>
        <td>${u.apptType}</td><td>${u.provider}</td>
        <td>${u.lastAttempt}</td><td>${u.noShowRisk}%</td>
      </tr>`).join('');
    const html = `
      <h1>Unconfirmed patients · manual outreach</h1>
      <p class="sub">${filteredUnconfirmed.length} patient(s) · Range: ${DATE_RANGES.find((r) => r.id === dateRange)?.label} · Type: ${typeFilter}${agentFilter !== 'all' ? ` · Agent: ${agents.find((a) => a.id === agentFilter)?.name}` : ''}</p>
      <table>
        <thead><tr><th>Patient</th><th>Phone</th><th>Appointment</th><th>Type</th><th>Provider</th><th>Last attempt</th><th>No-show %</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `;
    printHtml('Unconfirmed patients', html);
  };

  const exportScenarioCsv = () => {
    const rows = activeAgents.map((a) => {
      const s = (DEMO_PERFORMANCE_SERIES[a.id] || []).slice(-rangeDays);
      const confirmed = s.reduce((x, d) => x + d.confirmed, 0);
      const cancelled = s.reduce((x, d) => x + d.cancelled, 0);
      const pending = s.reduce((x, d) => x + d.pending, 0);
      const handled = confirmed + cancelled + pending;
      return { name: a.name, channel: a.channel, confirmed, cancelled, pending, handled, conversion: handled > 0 ? Math.round((confirmed / handled) * 100) : 0 };
    });
    const cols = [
      { key: 'name', label: 'Agent' }, { key: 'channel', label: 'Channel' },
      { key: 'handled', label: 'Tasks handled' }, { key: 'confirmed', label: 'Confirmed' },
      { key: 'pending', label: 'Pending' }, { key: 'cancelled', label: 'Cancelled' },
      { fn: (r) => `${r.conversion}%`, label: 'Conversion' },
    ];
    downloadCsv(`agent-scenario-report_${dateRange}.csv`, toCsv(rows, cols));
    toast.success('Scenario report exported');
  };

  const printScenario = () => {
    const rows = activeAgents.map((a) => {
      const s = (DEMO_PERFORMANCE_SERIES[a.id] || []).slice(-rangeDays);
      const confirmed = s.reduce((x, d) => x + d.confirmed, 0);
      const cancelled = s.reduce((x, d) => x + d.cancelled, 0);
      const pending = s.reduce((x, d) => x + d.pending, 0);
      const handled = confirmed + cancelled + pending;
      const conversion = handled > 0 ? Math.round((confirmed / handled) * 100) : 0;
      return `<tr><td>${a.name}</td><td>${a.channel}</td><td>${handled}</td><td>${confirmed}</td><td>${pending}</td><td>${cancelled}</td><td><b>${conversion}%</b></td></tr>`;
    }).join('');
    const html = `
      <h1>AI Agent performance · scenario report</h1>
      <p class="sub">Range: ${DATE_RANGES.find((r) => r.id === dateRange)?.label} · Type: ${typeFilter}${agentFilter !== 'all' ? ` · Agent: ${agents.find((a) => a.id === agentFilter)?.name}` : ''}</p>
      <div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Tasks handled</div><div class="kpi-val">${totals.handled}</div></div>
        <div class="kpi"><div class="kpi-lbl">Confirmed</div><div class="kpi-val">${totals.confirmed}</div></div>
        <div class="kpi"><div class="kpi-lbl">Pending</div><div class="kpi-val">${totals.pending}</div></div>
        <div class="kpi"><div class="kpi-lbl">Cancelled</div><div class="kpi-val">${totals.cancelled}</div></div>
        <div class="kpi"><div class="kpi-lbl">Conversion</div><div class="kpi-val">${totals.conversion}%</div></div>
      </div>
      <table>
        <thead><tr><th>Agent</th><th>Channel</th><th>Tasks</th><th>Confirmed</th><th>Pending</th><th>Cancelled</th><th>Conversion</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `;
    printHtml('Agent scenario report', html);
  };

  return (
    <div className="space-y-4" data-testid="agent-analytics-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[13px] font-semibold text-slate-900">Agent performance</p>
          <p className="text-[10px] text-slate-500">Totals, per-agent cards, exportable reports</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={filtersOpen ? 'default' : 'outline'} size="sm" className={cn('h-8 text-[11px] gap-1', filtersOpen && 'bg-[#111] hover:bg-[#222]')} onClick={() => setFiltersOpen((v) => !v)} data-testid="analytics-filters-toggle">
            <Filter className="h-3 w-3" /> Filters
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-[11px] gap-1" onClick={exportScenarioCsv} data-testid="export-scenario-csv">
            <Download className="h-3 w-3" /> CSV
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-[11px] gap-1" onClick={printScenario} data-testid="print-scenario">
            <Printer className="h-3 w-3" /> Print report
          </Button>
        </div>
      </div>

      {/* Filters */}
      {filtersOpen && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="analytics-filters">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase font-semibold text-slate-400 w-14">Range</span>
            {DATE_RANGES.map((r) => (
              <button key={r.id} onClick={() => setDateRange(r.id)}
                className={cn('px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors border',
                  dateRange === r.id ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100')}
                style={{ cursor: 'pointer' }}
                data-testid={`analytics-range-${r.id}`}
              >{r.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase font-semibold text-slate-400 w-14">Agent</span>
            <button onClick={() => setAgentFilter('all')}
              className={cn('px-2.5 py-1 rounded-md text-[10px] font-medium border transition-colors',
                agentFilter === 'all' ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100')}
              style={{ cursor: 'pointer' }}
              data-testid="analytics-agent-all"
            >All agents</button>
            {agents.map((a) => (
              <button key={a.id} onClick={() => setAgentFilter(a.id)}
                className={cn('px-2.5 py-1 rounded-md text-[10px] font-medium border transition-colors',
                  agentFilter === a.id ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100')}
                style={{ cursor: 'pointer' }}
                data-testid={`analytics-agent-${a.id}`}
              >{a.name.replace(' Agent', '')}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase font-semibold text-slate-400 w-14">Type</span>
            {APPT_TYPES.map((t) => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className={cn('px-2.5 py-1 rounded-md text-[10px] font-medium border transition-colors',
                  typeFilter === t ? 'bg-slate-100 text-slate-800 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100')}
                style={{ cursor: 'pointer' }}
                data-testid={`analytics-type-${t.replace(/\W+/g, '-').toLowerCase()}`}
              >{t}</button>
            ))}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-5 gap-3">
        <Kpi label="Tasks handled" value={totals.handled} sub={`${activeAgents.length} agent${activeAgents.length === 1 ? '' : 's'}`} icon={Users} testid="kpi-handled" />
        <Kpi label="Confirmed" value={totals.confirmed} sub="all channels" icon={CheckCircle2} tone="good" testid="kpi-confirmed" />
        <Kpi label="Pending" value={totals.pending} sub="awaiting patient" icon={Clock} tone="warn" testid="kpi-pending" />
        <Kpi label="Cancelled" value={totals.cancelled} sub="patient/agent" icon={X} tone="bad" testid="kpi-cancelled" />
        <Kpi label="Conversion" value={`${totals.conversion}%`} sub={totals.conversion >= 80 ? 'Above target' : 'Watch'} icon={totals.conversion >= 80 ? TrendingUp : TrendingDown} tone={totals.conversion >= 80 ? 'good' : 'warn'} testid="kpi-conversion" />
      </div>

      {/* Per-agent cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {activeAgents.map((a) => {
          const s = (DEMO_PERFORMANCE_SERIES[a.id] || []).slice(-rangeDays);
          const confirmed = s.reduce((x, d) => x + d.confirmed, 0);
          const cancelled = s.reduce((x, d) => x + d.cancelled, 0);
          const pending = s.reduce((x, d) => x + d.pending, 0);
          const handled = confirmed + cancelled + pending;
          const conv = handled > 0 ? Math.round((confirmed / handled) * 100) : 0;
          const ChIcon = a.channel.includes('voice') ? Phone : MessageSquareText;
          return (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-3.5 hover:border-slate-400 transition-colors cursor-pointer" data-testid={`perf-card-${a.id}`} onClick={() => setAgentFilter(a.id)} title={`Click to scope all filters + exports to ${a.name}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <ChIcon className="h-3.5 w-3.5 text-slate-500" />
                    <p className="text-[13px] font-semibold text-slate-900">{a.name}</p>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">{a.trigger}</p>
                </div>
                <Badge className={cn('text-[9px] border', conv >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : conv >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200')}>
                  {conv}% conv
                </Badge>
              </div>
              <div className="mt-3">
                <SparkBars series={s} />
                <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-300" /> {confirmed}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-300" /> {pending}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-300" /> {cancelled}</span>
                  <span className="ml-auto">· {handled} handled</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Campaign sends — outbound messages from "Send now" runs */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="campaign-sends-panel">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/40">
          <div className="flex items-center gap-2">
            <Zap className="h-3.5 w-3.5 text-slate-700" />
            <p className="text-[12px] font-semibold text-slate-900">Campaign sends</p>
            <Badge className="text-[9px] border border-emerald-200 bg-emerald-50 text-emerald-800">{mergedTotals.sent} sent</Badge>
            {mergedTotals.failed > 0 && (
              <Badge className="text-[9px] border border-red-200 bg-red-50 text-red-700">{mergedTotals.failed} failed</Badge>
            )}
            <Badge className="text-[9px] border border-slate-200 bg-white text-slate-600">{mergedTotals.total} total</Badge>
          </div>
          <p className="text-[10px] text-slate-400">Updated live · from "Send now" + Smart-hold recoveries</p>
        </div>
        {mergedSends.length === 0 ? (
          <div className="p-6 text-center" data-testid="campaign-sends-empty">
            <Sparkles className="h-4 w-4 text-slate-300 mx-auto mb-1.5" />
            <p className="text-[11px] font-medium text-slate-700">No campaign sends yet.</p>
            <p className="text-[10px] text-slate-500">Tap "Send now" on an Online Booking Outreach agent to log a batch.</p>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {mergedSends.slice(0, 12).map((s) => (
              <div key={s.send_id} className="px-4 py-2 flex items-center gap-3" data-testid={`campaign-send-${s.send_id}`}>
                <Send className={cn('h-3 w-3 shrink-0', s.status === 'sent' ? 'text-emerald-600' : 'text-red-500')} />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-800 truncate">
                    {s.to_name || s.to} <span className="text-slate-400">· {s.channel.toUpperCase()}</span>
                    {s.kind === 'smart_hold_recovery' && (
                      <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">smart hold</span>
                    )}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">{s.body}</p>
                </div>
                <p className="text-[9px] text-slate-400 shrink-0">
                  {s.sent_at ? new Date(s.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unconfirmed patient list */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="unconfirmed-list">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-amber-50/40">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <p className="text-[12px] font-semibold text-slate-900">Patients not yet confirmed</p>
            <Badge className="text-[9px] border border-amber-200 bg-amber-100 text-amber-800">{filteredUnconfirmed.length}</Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={exportUnconfirmedCsv} data-testid="export-unconfirmed-csv">
              <Download className="h-3 w-3" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={printUnconfirmed} data-testid="print-unconfirmed">
              <Printer className="h-3 w-3" /> Print list
            </Button>
          </div>
        </div>
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-[9px] text-slate-400 uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-1.5">Patient</th>
              <th className="text-left px-3 py-1.5">Phone</th>
              <th className="text-left px-3 py-1.5">When</th>
              <th className="text-left px-3 py-1.5">Type</th>
              <th className="text-left px-3 py-1.5">Last attempt</th>
              <th className="text-right px-3 py-1.5">Risk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUnconfirmed.length === 0 ? (
              <tr><td colSpan={6} className="text-center px-3 py-6 text-[11px] text-slate-400">All patients confirmed — no outreach needed.</td></tr>
            ) : filteredUnconfirmed.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50/60" data-testid={`unconfirmed-row-${u.id}`}>
                <td className="px-3 py-1.5 font-medium text-slate-800">{u.patient}</td>
                <td className="px-3 py-1.5 text-slate-600"><a href={`tel:${u.phone}`} className="underline decoration-slate-300 hover:decoration-slate-800">{u.phone}</a></td>
                <td className="px-3 py-1.5 text-slate-600">{u.apptTime}</td>
                <td className="px-3 py-1.5 text-slate-600">{u.apptType}</td>
                <td className="px-3 py-1.5 text-slate-500">{u.lastAttempt}</td>
                <td className="px-3 py-1.5 text-right">
                  <span className={cn('text-[10px] font-semibold', u.noShowRisk >= 50 ? 'text-red-600' : u.noShowRisk >= 30 ? 'text-amber-700' : 'text-slate-500')}>{u.noShowRisk}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, icon: Icon, tone, testid }) => {
  const toneClass = tone === 'good' ? 'text-emerald-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-red-600' : 'text-slate-500';
  return (
    <div className="p-3.5 rounded-xl bg-white border border-slate-200" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] text-slate-400">{label}</p>
        {Icon && <Icon className={cn('h-3 w-3', toneClass)} />}
      </div>
      <p className="text-[22px] font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-[9px] text-slate-400 mt-1">{sub}</p>
    </div>
  );
};

export default AgentAnalyticsPanel;
